import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import FlashMessage from '../../components/FlashMessage/index';
import { LOGOUT_SESSION_STORE_KEY } from '../../redux/auth/auth.actions';
import {
  DEACTIVATE_CLAIM_STORE_KEY,
  EXTEND_RESPONSE_DEADLINE_STORE_KEY,
  RESOLVE_CLAIM_STORE_KEY,
  SET_PAID_AT_CLAIM_STORE_KEY,
  UPDATE_AUTO_CHARGE_STORE_KEY,
  UPDATE_BANK_TRANSACTIONS_ENABLED_AT_STORE_KEY,
} from '../../redux/claim/claim.actions';
import { dismissFlashMessage } from '../../redux/flash-messages/flash-messages.actions';
import {
  createFailedFlashMessagesSelector,
  createFlashMessagesSelector,
} from '../../redux/flash-messages/flash-messages.selector';
import { FlashMessagePayload } from '../../redux/flash-messages/flash-messages.types';
import { NOTIFY_INTEGRATOR_STORE_KEY } from '../../redux/integrator/integrator.actions';
import {
  CREATE_ORGANIZATION_INVITE_STORE_KEY,
  CREATE_ORGANIZATION_STORE_KEY,
  UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY,
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY,
  UPDATE_ORGANIZATION_NAME_STORE_KEY,
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY,
  UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY,
} from '../../redux/organization/organization.actions';
import {
  ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
  UNASSIGN_ACCOUNT_MANAGER_STORE_KEY,
} from '../../redux/organization-internal-owner/organization-internal-owner.actions';
import {
  CREATE_PAYMENT_PLAN_STORE_KEY,
  DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  DELETE_PAYMENT_PLAN_STORE_KEY,
  FORCE_DELETE_PAYMENT_PLAN_STORE_KEY,
} from '../../redux/payment-plan/payment-plan.actions';
import {
  CHECKOUT_REPOSIT_STORE_KEY,
  DISCARD_REPOSIT_STORE_KEY,
  EXTEND_REPOSIT_STORE_KEY,
  PUBLISH_REPOSIT_STORE_KEY,
  RESEND_TENANT_INVITE_STORE_KEY,
} from '../../redux/reposit/reposit.actions';
import {
  BLOCK_TENANT_STORE_KEY,
  FETCH_BLOCKED_TENANT_STORE_KEY,
  RESET_USER_PASSWORD_STORE_KEY,
  UNBLOCK_TENANT_STORE_KEY,
} from '../../redux/user/user.actions';

export const subscribedMessages: string[] = [
  DISCARD_REPOSIT_STORE_KEY,
  PUBLISH_REPOSIT_STORE_KEY,
  CHECKOUT_REPOSIT_STORE_KEY,
  EXTEND_REPOSIT_STORE_KEY,
  RESEND_TENANT_INVITE_STORE_KEY,
  RESOLVE_CLAIM_STORE_KEY,
  DEACTIVATE_CLAIM_STORE_KEY,
  UPDATE_AUTO_CHARGE_STORE_KEY,
  UPDATE_BANK_TRANSACTIONS_ENABLED_AT_STORE_KEY,
  EXTEND_RESPONSE_DEADLINE_STORE_KEY,
  SET_PAID_AT_CLAIM_STORE_KEY,
  CREATE_PAYMENT_PLAN_STORE_KEY,
  DELETE_PAYMENT_PLAN_STORE_KEY,
  FORCE_DELETE_PAYMENT_PLAN_STORE_KEY,
  DELETE_PAYMENT_PLAN_INSTALMENT_STORE_KEY,
  CREATE_ORGANIZATION_INVITE_STORE_KEY,
  CREATE_ORGANIZATION_STORE_KEY,
  UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY,
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY,
  UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY,
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY,
  UPDATE_ORGANIZATION_NAME_STORE_KEY,
  NOTIFY_INTEGRATOR_STORE_KEY,
  RESET_USER_PASSWORD_STORE_KEY,
  BLOCK_TENANT_STORE_KEY,
  FETCH_BLOCKED_TENANT_STORE_KEY,
  UNBLOCK_TENANT_STORE_KEY,
  ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
  UNASSIGN_ACCOUNT_MANAGER_STORE_KEY,
  LOGOUT_SESSION_STORE_KEY,
];

const StyledFlashMessageContainer = styled.div`
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  margin: 24px 0 0 240px;
  padding: 0 50px;
  width: calc(100% - 240px);
  z-index: 9000;
`;

export const FLASH_MESSAGE_TIMEOUT = 5000;

export const useFlashMessage = (storeKeys: string[]): [FlashMessagePayload, () => void] => {
  const dispatch = useDispatch();
  const getFlashMessageSelector = createFlashMessagesSelector(storeKeys);
  const flashMessage = useSelector(getFlashMessageSelector);
  const onDismissFlashMessage = () => dispatch(dismissFlashMessage(storeKeys));

  return [flashMessage, onDismissFlashMessage];
};

export const useFailedFlashMessage = (storeKeys: string[]): [FlashMessagePayload, () => void] => {
  const dispatch = useDispatch();
  const getFlashMessageSelector = createFailedFlashMessagesSelector(storeKeys);
  const flashMessage = useSelector(getFlashMessageSelector);
  const onDismissFlashMessage = () => dispatch(dismissFlashMessage(storeKeys));

  return [flashMessage, onDismissFlashMessage];
};

const FlashMessageContainer: React.FC = () => {
  const [flashMessage, onDismissFlashMessage] = useFlashMessage(subscribedMessages);
  return flashMessage ? (
    <StyledFlashMessageContainer>
      <FlashMessage onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} payload={flashMessage} />
    </StyledFlashMessageContainer>
  ) : null;
};

export default FlashMessageContainer;
